/**
 * Add `OrderFlowRouteName` to the `RouteName` type, and `routeNames` object in
 * the app.
 *
 * Example:
 * ```
 * export type RouteName = ProRouteName | OrderFlowRouteName;
 *
 * export const routeNames = {
 *   ...ProRouteName,
 *   ...OrderFlowRouteName,
 * };
 * ```
 */
var OrderFlowRouteName;
(function (OrderFlowRouteName) {
    /**
     * Bulk order option for dashboard users
     */
    OrderFlowRouteName["ORDER_FLOW_BULK_UPLOAD"] = "OrderFlow/BulkUpload";
    /** `ORDER_FLOW_BULK_UPLOAD` if navigated to from the visit overview. */
    OrderFlowRouteName["ORDER_FLOW_BULK_UPLOAD_AS_CHILD"] = "OrderFlow/VisitOverview/BulkUpload";
    /**
     * Potentially the first step of the order flow for a new order.
     * Otherwise, substep of Visit Overview page.
     *
     * The user can upload supporting documents for a particular
     * booking on the order.
     */
    OrderFlowRouteName["ORDER_FLOW_PDF_UPLOAD"] = "OrderFlow/Order";
    /** `ORDER_FLOW_PDF_UPLOAD` if navigated to from the visit overview. */
    OrderFlowRouteName["ORDER_FLOW_PDF_UPLOAD_AS_CHILD"] = "OrderFlow/VisitOverview/Order";
    /**
     * Potentially the first step of the order flow for a new order.
     * Otherwise, substep of Visit Overview page.
     *
     * The user can input patient information manually
     */
    OrderFlowRouteName["ORDER_FLOW_MANUAL_INPUT"] = "OrderFlow/Order/OrderFlowManualInput";
    /** `ORDER_FLOW_MANUAL_INPUT` if navigated to from the visit overview. */
    OrderFlowRouteName["ORDER_FLOW_MANUAL_INPUT_AS_CHILD"] = "OrderFlow/VisitOverview/Order/OrderFlowManualInput";
    /**
     * Potentially the second step of the order flow for a new order (after pdf input).
     * Otherwise, the second substep of the Visit Overview page if input flow type is pdf.
     *
     * The user can input additional information
     */
    OrderFlowRouteName["ORDER_FLOW_ADDITIONAL_INFORMATION"] = "OrderFlow/Order/OrderFlowAdditionalInformation";
    /** `ORDER_FLOW_ADDITIONAL_INFORMATION` if navigated to from the visit overview. */
    OrderFlowRouteName["ORDER_FLOW_ADDITIONAL_INFORMATION_AS_CHILD"] = "OrderFlow/VisitOverview/Order/OrderFlowAdditionalInformation";
    /**
     * Potentially the second step of the order flow for a new order (after manual input).
     * Otherwise, the second substep of the Visit Overview page if input flow type is manual.
     *
     * The user can input patient information manually
     */
    OrderFlowRouteName["ORDER_FLOW_ORDER_MEDICAL_DETAILS"] = "OrderFlow/Order/OrderFlowMedicalDetails";
    /** `ORDER_FLOW_ORDER_MEDICAL_DETAILS` if navigated to from the visit overview. */
    OrderFlowRouteName["ORDER_FLOW_ORDER_MEDICAL_DETAILS_AS_CHILD"] = "OrderFlow/VisitOverview/Order/OrderFlowMedicalDetails";
    /**
     * Potentially the first step of the order flow for a new order.
     * Otherwise, substep of Visit Overview page.
     *
     * The user can indicate who will be providing the order.
     */
    OrderFlowRouteName["ORDER_FLOW_ORDER_PROVIDER"] = "OrderFlow/OrderProvider";
    /** `ORDER_FLOW_ORDER_PROVIDER` if navigated to from the visit overview. */
    OrderFlowRouteName["ORDER_FLOW_ORDER_PROVIDER_AS_CHILD"] = "OrderFlow/VisitOverview/OrderProvider";
    /**
     * The second step of the order flow for a new order.
     *
     * The user can configure who will complete the order (CarePro or in-office).
     */
    OrderFlowRouteName["ORDER_FLOW_ORDER_FULFILLER"] = "OrderFlow/OrderFulfiller";
    /**
     * The third step of the order flow for a new order.
     *
     * The user can configure how the order will be scheduled.
     */
    OrderFlowRouteName["ORDER_FLOW_SCHEDULE"] = "OrderFlow/Schedule";
    /**
     * `ORDER_FLOW_SCHEDULE` if navigated to from the visit overview.
     *
     * This is a standalone substep of Visit Overview page, separate from the
     * booking subflows. The associated ID is the draft order ID.
     */
    OrderFlowRouteName["ORDER_FLOW_SCHEDULE_AS_CHILD"] = "OrderFlow/VisitOverview/Schedule";
    /**
     * The last step of the order flow for a new order. When a user loads an
     * existing draft order, they will be directed to this step, but they will
     * still be able to go back to previous steps.
     *
     * The user sees an overview or the order setup, and can add additional
     * patients to the visit.
     */
    OrderFlowRouteName["ORDER_FLOW_VISIT_OVERVIEW"] = "OrderFlow/VisitOverview";
})(OrderFlowRouteName || (OrderFlowRouteName = {}));

export { OrderFlowRouteName };
